import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useI18n } from 'builder/hooks/useI18n'
import { useConfig } from 'builder/hooks/useConfig'
import { useUser } from 'builder/hooks/useUser'
import { ButtonTheme } from 'builder/components/Button'
import { ApiPlan } from 'builder/modules/billing/types'
import { actions as billingActions, selectors } from 'builder/modules/billing/billingModule'
import { WizardHeader } from 'builder/components/Billing/WizardHeader/WizardHeader'
import { TrustpilotRatingHorizontal } from 'builder/components/TrustpilotRating/TrustpilotRatingHorizontal'
import { SubscriptionBenefits } from 'builder/components/Billing/SubscriptionBenefits/SubscriptionBenefits'
import { Logos } from 'builder/components/Logos/Logos'
import { Spinner } from 'builder/components/Spinner'
import { Logo } from 'builder/components/Logo'

import CLOCK from './assets/icons/clock.svg'
import DOCUMENT from './assets/icons/document.svg'
import DOCUMENTS from './assets/icons/documents.svg'
import GROUP from './assets/icons/group.svg'
import MONEY from './assets/icons/money.svg'
import GRAPH from './assets/icons/graph.svg'
import STAR from './assets/icons/star.svg'

import {
  Container,
  PlansRedesignRoot,
  PlansBenefitsWrapper,
  PlansBenefitsTitle,
  PlansBenefitsScreen,
  PlansBenefitsLeft,
  PlansBenefitsContainer,
  PricingOption,
  RadioButton,
  PricingOptionContent,
  PricingDetails,
  PricingPeriod,
  PricingAmount,
  PlanFeatures,
  PlanFeature,
  FeatureIcon,
  PlanFeatureDescription,
  ContinueButton,
  MobileBottomContainer,
  CancelAnytime,
  MostPopularBadge,
  PeopleChoseBubble,
  HeaderContainer,
  DurationSuffix,
  CloseButton,
} from './styles'

const getPeopleCount = () => {
  const storageKey = 'plans_people_count'
  let cachedCount = localStorage.getItem(storageKey)

  if (!cachedCount) {
    const minCount = Math.floor(3000 * 0.9)
    const maxCount = Math.floor(3000 * 1.1)
    const randomCount = Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount
    cachedCount = randomCount.toString()
    localStorage.setItem(storageKey, cachedCount)
  }

  return cachedCount
}

export const PlansView = () => {
  const { isPhone, isTablet } = useMediaQueries()
  const { i18n } = useI18n()
  const config = useConfig()
  const user = useUser()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [selectedPlan, setSelectedPlan] = useState('')
  const plans = useSelector(selectors.availablePlans)
  const scenarioName = useSelector(selectors.scenarioName)
  const isLoading = useSelector(selectors.plansLoading)

  const isHistoricalLeads = scenarioName === 'historical_leads'

  const hostname = window.location.hostname
  const isCareerIo = hostname === 'career.io' || hostname === 'staging.career.io'
  const isUS = config?.features?.geoipCode === 'US'

  useEffect(() => {
    dispatch(billingActions.fetchAvailablePlans())
    dispatch(billingActions.fetchScenario())
  }, [dispatch])

  useEffect(() => {
    if (plans.length > 0 && !selectedPlan) {
      setSelectedPlan(plans[0].name)
    }
  }, [plans, selectedPlan])

  const mainPlan = plans[0]
  const secondaryPlan = plans[1]

  const formatPlanAmount = (plan: ApiPlan) => {
    if (plan.hasTrial && plan.trialDays && plan.trialDays > 0 && !user?.isPostPremium) {
      return `${plan.currencySymbol}${((plan.amount || 0) / 100).toFixed(2)}`
    }
    return plan.formattedRecurringAmount
  }

  const formatPlanPeriod = (plan: ApiPlan) => {
    if (plan.hasTrial && plan.trialDays && plan.trialDays > 0) {
      if (user?.isPostPremium) {
        const isMonthly =
          (plan.interval === 'month' && plan.intervalCount === 1) ||
          (plan.interval === 'week' && plan.intervalCount === 4)
        const isQuarterly = plan.interval === 'month' && plan.intervalCount === 3
        const isYearly = plan.interval === 'year' && plan.intervalCount === 1

        if (isYearly) {
          return i18n.t('builder.billing.plans.periods.one_year')
        } else if (isQuarterly) {
          return i18n.t('builder.billing.plans.periods.three_months')
        } else if (isMonthly) {
          return plan.interval === 'week'
            ? i18n.t('builder.billing.plans.periods.four_weeks')
            : i18n.t('builder.billing.plans.periods.one_month')
        }

        if (plan.interval && plan.intervalCount) {
          const count = plan.intervalCount
          const interval = plan.interval
          if (count === 1) {
            return `1 ${interval}`
          } else {
            const pluralInterval = `${interval}s`
            return `${count} ${pluralInterval}`
          }
        }

        return plan.name
      }
      return i18n.t('builder.billing.plans.periods.trial_format', { count: plan.trialDays })
    }

    if (plan.caption && plan.caption.trim() !== '') {
      return plan.caption
    }

    if (plan.interval && plan.intervalCount) {
      const count = plan.intervalCount
      const interval = plan.interval

      if (count === 1) {
        return `1 ${interval}`
      } else {
        const pluralInterval = `${interval}s`
        return `${count} ${pluralInterval}`
      }
    }

    return plan.name
  }

  const handleContinue = (planId: string) => {
    window.location.href = `/app/billing/payment?plan=${planId}`
  }

  const generatePricingText = (plan: ApiPlan | undefined) => {
    if (!plan) return { text: '', hasDiscount: false }
    if (!plan.isRecurring)
      return { text: i18n.t('builder.billing.plans.billing.one_time_payment'), hasDiscount: false }

    const isMonthly =
      (plan.interval === 'month' && plan.intervalCount === 1) ||
      (plan.interval === 'week' && plan.intervalCount === 4)
    const isQuarterly = plan.interval === 'month' && plan.intervalCount === 3
    const isYearly = plan.interval === 'year' && plan.intervalCount === 1
    const hasTrial = plan.hasTrial && plan.trialDays && plan.trialDays > 0 && !user?.isPostPremium
    const trialDays = plan.trialDays || 0
    const originalPrice = plan.formattedAmountBeforeDiscount || plan.formattedRecurringAmount
    const discountedPrice = plan.formattedRecurringAmount

    const trialText = hasTrial
      ? i18n.t('builder.billing.plans.periods.after_trial', { count: trialDays })
      : ''
    let billingText = ''

    if (isMonthly) billingText = i18n.t('builder.billing.plans.billing.billed_monthly')
    else if (isQuarterly) billingText = i18n.t('builder.billing.plans.billing.billed_quarterly')
    else if (isYearly) billingText = i18n.t('builder.billing.plans.billing.billed_yearly')

    const baseText = isQuarterly
      ? i18n.t('builder.billing.plans.billing.auto_renews_to')
      : i18n.t('builder.billing.plans.billing.auto_renews_at')

    const discountKey = isQuarterly
      ? 'pricing_text_quarterly_with_discount'
      : 'pricing_text_with_discount'
    const regularKey = isQuarterly
      ? 'pricing_text_quarterly_without_discount'
      : 'pricing_text_without_discount'

    if (plan.hasDiscount) {
      return {
        text: i18n.t(`builder.billing.plans.billing.${discountKey}`, {
          base_text: baseText,
          trial_text: trialText,
          billing_text: billingText,
        }),
        beforePrice: baseText,
        afterPrice: i18n.t('builder.billing.plans.billing.pricing_after_discount', {
          trial_text: trialText,
          billing_text: billingText,
        }),
        originalPrice,
        discountedPrice,
        hasDiscount: true,
      }
    }

    return {
      text: i18n.t(`builder.billing.plans.billing.${regularKey}`, {
        base_text: baseText,
        price: originalPrice,
        trial_text: trialText,
        billing_text: billingText,
      }),
      hasDiscount: false,
    }
  }

  const selectedPlanData = plans.find(plan => plan.name === selectedPlan)
  const pricingData = generatePricingText(selectedPlanData)

  const getButtonTheme = () => {
    const isFirstPlan = selectedPlan === plans[0]?.name
    return isFirstPlan ? ButtonTheme.confirmative : ButtonTheme.default
  }

  const getPricingFeature = (plan: ApiPlan | undefined) => {
    if (!plan || !plan.isRecurring) {
      return null
    }

    const hasTrial = plan.hasTrial && plan.trialDays && plan.trialDays > 0 && !user?.isPostPremium
    const price = plan.formattedRecurringAmount

    let durationInWeeks = 0
    if (plan.interval === 'day') {
      durationInWeeks = (plan.intervalCount || 1) / 7
    } else if (plan.interval === 'week') {
      durationInWeeks = plan.intervalCount || 1
    } else if (plan.interval === 'month') {
      durationInWeeks = (plan.intervalCount || 1) * 4
    } else if (plan.interval === 'year') {
      durationInWeeks = (plan.intervalCount || 1) * 52
    }

    let periodText = i18n.t('builder.billing.plans.billing.per_month')
    if (durationInWeeks < 4) {
      if (plan.interval === 'day') {
        const dayCount = plan.intervalCount || 1
        if (dayCount === 7) {
          periodText = i18n.t('builder.billing.plans.billing.per_week')
        } else {
          periodText = i18n.t('builder.billing.plans.billing.per_day')
        }
      } else if (plan.interval === 'week') {
        periodText = i18n.t('builder.billing.plans.billing.per_week')
      }
    }

    if (hasTrial) {
      return i18n.t('builder.billing.plans.billing.just_after_trial', { price, period: periodText })
    } else {
      return i18n.t('builder.billing.plans.billing.just_price', { price, period: periodText })
    }
  }

  const pricingFeatureText = getPricingFeature(selectedPlanData)

  const features = isHistoricalLeads
    ? [
        {
          icon: DOCUMENTS,
          boldText: i18n.t(
            'builder.billing.plans.historical_leads.features.unlimited_resumes.bold',
          ),
          text: i18n.t('builder.billing.plans.historical_leads.features.unlimited_resumes.text'),
        },
        {
          icon: STAR,
          boldText: i18n.t('builder.billing.plans.historical_leads.features.ai_interview.bold'),
          text: i18n.t('builder.billing.plans.historical_leads.features.ai_interview.text'),
        },
        {
          icon: GRAPH,
          boldText: i18n.t('builder.billing.plans.historical_leads.features.learning_library.bold'),
          text: i18n.t('builder.billing.plans.historical_leads.features.learning_library.text'),
        },
        {
          icon: GROUP,
          boldText: i18n.t(
            'builder.billing.plans.historical_leads.features.recruiter_connections.bold',
          ),
          text: i18n.t(
            'builder.billing.plans.historical_leads.features.recruiter_connections.text',
          ),
        },
        ...(pricingFeatureText
          ? [
              {
                icon: MONEY,
                boldText: pricingFeatureText,
                text: i18n.t('builder.billing.plans.historical_leads.affordable'),
              },
            ]
          : []),
      ]
    : [
        {
          icon: DOCUMENTS,
          boldText: i18n.t('builder.billing.plans.benefits.features.one.bold'),
          text: i18n.t('builder.billing.plans.benefits.features.one.text'),
        },
        {
          icon: DOCUMENT,
          text: i18n.t('builder.billing.plans.benefits.features.two'),
        },
        {
          icon: GROUP,
          text: i18n.t('builder.billing.plans.benefits.features.three'),
        },
        {
          icon: CLOCK,
          pricingData: pricingData,
        },
        {
          icon: MONEY,
          boldText: i18n.t('builder.billing.plans.benefits.features.five'),
        },
      ]

  const getDurationSuffix = (plan: ApiPlan) => {
    if (!plan.isRecurring) return ''

    const isMonthly =
      (plan.interval === 'month' && plan.intervalCount === 1) ||
      (plan.interval === 'week' && plan.intervalCount === 4)
    const isQuarterly = plan.interval === 'month' && plan.intervalCount === 3
    const isYearly = plan.interval === 'year' && plan.intervalCount === 1
    const isWeekly = plan.interval === 'week' && plan.intervalCount === 1
    const isDaily = plan.interval === 'day' && plan.intervalCount === 1

    if (isDaily) {
      return i18n.t('builder.billing.plans.benefits.day')
    } else if (isWeekly) {
      return i18n.t('builder.billing.plans.benefits.week')
    } else if (isMonthly || isQuarterly) {
      return i18n.t('builder.billing.plans.benefits.mo')
    } else if (isYearly) {
      return i18n.t('builder.billing.plans.benefits.year')
    }

    return ''
  }

  const getAdjustedPlanAmount = (plan: ApiPlan) => {
    if (plan.isRecurring && plan.interval === 'month' && plan.intervalCount === 3) {
      const monthlyAmountInCents = plan.recurringAmount / 3
      const monthlyAmount = (monthlyAmountInCents / 100).toFixed(2)
      return `${plan.currencySymbol}${monthlyAmount}`
    }

    return formatPlanAmount(plan)
  }

  const renderPlanOption = (plan: ApiPlan, index: number) => {
    const isSelected = selectedPlan === plan.name
    const isFirstPlan = index === 0
    const isSecondPlan = index === 1
    const durationSuffix = isSecondPlan ? getDurationSuffix(plan) : ''

    return (
      <PricingOption
        key={plan.name}
        $selected={isSelected}
        onClick={() => setSelectedPlan(plan.name)}
      >
        {isFirstPlan && !isPhone && (
          <PeopleChoseBubble>
            {i18n.t('builder.billing.plans.benefits.people_chose_this_hours', {
              number: getPeopleCount(),
            })}
          </PeopleChoseBubble>
        )}
        <PricingOptionContent>
          <RadioButton $selected={isSelected} />
          <PricingDetails>
            <PricingPeriod>{formatPlanPeriod(plan)}</PricingPeriod>
            <PricingAmount>
              {getAdjustedPlanAmount(plan)}
              {durationSuffix && <DurationSuffix>{durationSuffix}</DurationSuffix>}
            </PricingAmount>
          </PricingDetails>
        </PricingOptionContent>
        {isFirstPlan && (
          <MostPopularBadge>
            {i18n.t('builder.billing.plans.benefits.most_popular')}
          </MostPopularBadge>
        )}
      </PricingOption>
    )
  }

  if (isLoading) {
    return (
      <Container>
        <Spinner $width="64px" $height="64px" />
      </Container>
    )
  }

  return (
    <Container>
      <HeaderContainer>
        <Logo variant="default" />
      </HeaderContainer>
      <WizardHeader step={2} />
      <CloseButton onClick={() => navigate('/')} />
      <PlansRedesignRoot>
        <PlansBenefitsWrapper>
          <PlansBenefitsTitle>
            {isHistoricalLeads
              ? i18n.t('builder.billing.plans.historical_leads.title')
              : i18n.t('builder.billing.plans.benefits.title')}
          </PlansBenefitsTitle>

          <PlansBenefitsScreen>
            <PlansBenefitsLeft>
              <PlansBenefitsContainer>
                {mainPlan && renderPlanOption(mainPlan, 0)}
                {secondaryPlan && renderPlanOption(secondaryPlan, 1)}
              </PlansBenefitsContainer>

              <PlanFeatures>
                {features.map((feature, index) => (
                  <PlanFeature key={index}>
                    <FeatureIcon src={feature.icon} alt="" />
                    <PlanFeatureDescription $isHistoricalLeads={isHistoricalLeads}>
                      {feature.boldText && (
                        <PlanFeatureDescription
                          className="bold"
                          $isHistoricalLeads={isHistoricalLeads}
                        >
                          {feature.boldText}
                        </PlanFeatureDescription>
                      )}
                      {feature.pricingData ? (
                        feature.pricingData.hasDiscount ? (
                          <>
                            {feature.pricingData.beforePrice}
                            <del>{feature.pricingData.originalPrice}</del>{' '}
                            <strong>{feature.pricingData.discountedPrice}</strong>
                            {feature.pricingData.afterPrice}
                          </>
                        ) : (
                          feature.pricingData.text
                        )
                      ) : (
                        feature.text && (
                          <PlanFeatureDescription $isHistoricalLeads={isHistoricalLeads}>
                            {' '}
                            {feature.text}
                          </PlanFeatureDescription>
                        )
                      )}
                    </PlanFeatureDescription>
                  </PlanFeature>
                ))}
              </PlanFeatures>

              {isTablet && !isPhone && (
                <SubscriptionBenefits
                  continueOnClick={() => handleContinue(selectedPlan)}
                  theme={getButtonTheme()}
                  isHistoricalLeads={isHistoricalLeads}
                />
              )}

              <TrustpilotRatingHorizontal />
            </PlansBenefitsLeft>

            {!isTablet && !isPhone && (
              <SubscriptionBenefits
                continueOnClick={() => handleContinue(selectedPlan)}
                theme={getButtonTheme()}
                isHistoricalLeads={isHistoricalLeads}
              />
            )}
          </PlansBenefitsScreen>
        </PlansBenefitsWrapper>

        <Logos />

        {isPhone && (
          <MobileBottomContainer>
            <ContinueButton onClick={() => handleContinue(selectedPlan)} theme={getButtonTheme()}>
              {isHistoricalLeads
                ? i18n.t('builder.billing.plans.historical_leads.button')
                : i18n.t('builder.billing.plans.benefits.button')}
            </ContinueButton>

            <CancelAnytime>
              {i18n.t('builder.billing.plans.benefits.cancel_anytime')}
              <CancelAnytime className="email">
                {isUS ? '+1 (833) 998-8370' : `support@${isCareerIo ? 'career' : 'resume'}.io`}
              </CancelAnytime>
            </CancelAnytime>
          </MobileBottomContainer>
        )}
      </PlansRedesignRoot>
    </Container>
  )
}
